/**
 * PortfolioShareModal — lets a visitor email their completed maturity profile
 * to Ekselens. Self-contained: owns the form + submit + success state.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, X } from "lucide-react";
import { getDeviceId } from "../../lib/deviceId";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PortfolioShareModal({ open, onClose, snapshots, completedCount, context }) {
  const c = {
    eyebrow: "EKSELENS360 · Portfolio share",
    title: `Send your ${completedCount}-sub-brand maturity profile to Ekselens.`,
    blurb: "We'll come back to you within one business day with a sector-calibrated, founder-led perspective across your strongest and weakest sub-brands. No slide pitch, no obligation.",
    consentLabel: "I'd like Ekselens to follow up about my portfolio maturity. I understand my scores and contact details will be stored to enable this conversation.",
    submitLabel: "Send portfolio",
    counterLabel: `Sharing ${completedCount} of 12 sub-brand scores`,
    successBlurb: "A one-page branded PDF of your portfolio is being emailed to you now. A founder-led conversation will follow within one business day with a sector-calibrated next step across your strongest and weakest sub-brands.",
    ...(context || {}),
  };
  const [form, setForm] = useState({ full_name: "", email: "", organisation: "", role: "", sector: "", note: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please tick the consent box to proceed.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/public/maturity/portfolio`, {
        snapshots,
        ...form,
        device_id: getDeviceId(),
      });
      setDone(true);
      toast.success(`Portfolio shared — a one-page PDF is on its way to ${form.email}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send portfolio. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="portfolio-share-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-lg w-full p-7 md:p-9 relative text-black"
      >
        <button type="button" onClick={onClose} aria-label="Close" className="absolute top-3 right-3 text-zinc-500 hover:text-black" data-testid="portfolio-share-close">
          <X size={18} />
        </button>
        {done ? (
          <div className="text-center py-4" data-testid="portfolio-share-success">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#7737F6] text-white rounded-full mb-4"><CheckCircle2 size={24} /></div>
            <h3 className="ek-heading text-xl md:text-2xl mb-2">Thanks — your portfolio is on the way.</h3>
            <p className="text-zinc-600 text-[14px]">{c.successBlurb}</p>
            <button type="button" onClick={onClose} className="ek-btn ek-btn-outline mt-6 text-[13px]">Close</button>
          </div>
        ) : (
          <>
            <div className="ek-eyebrow text-[#7737F6]">{c.eyebrow}</div>
            <h3 className="ek-heading text-xl md:text-2xl mt-2">{c.title}</h3>
            <p className="ek-body text-[13.5px] mt-3 text-zinc-600">
              {c.blurb}
            </p>
            <form onSubmit={submit} className="mt-5 space-y-2.5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name *" className="border border-zinc-300 px-3 py-2 text-[16px]" data-testid="portfolio-share-name" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email *" className="border border-zinc-300 px-3 py-2 text-[16px]" data-testid="portfolio-share-email" />
                <input value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} placeholder="Organisation" className="border border-zinc-300 px-3 py-2 text-[16px]" data-testid="portfolio-share-org" />
                <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role" className="border border-zinc-300 px-3 py-2 text-[16px]" data-testid="portfolio-share-role" />
                <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="border border-zinc-300 px-3 py-2 text-[16px] md:col-span-2" data-testid="portfolio-share-sector">
                  <option value="">Sector (optional)</option>
                  <option>Manufacturing</option>
                  <option>Telecom</option>
                  <option>Financial services</option>
                  <option>Public sector</option>
                  <option>Retail</option>
                  <option>Healthcare</option>
                  <option>Energy & utilities</option>
                  <option>Professional services</option>
                  <option>Other</option>
                </select>
              </div>
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Anything specific you'd like us to focus on? (optional)" rows={3} className="w-full border border-zinc-300 px-3 py-2 text-[16px]" data-testid="portfolio-share-note" />
              <label className="flex items-start gap-2 text-[12px] text-zinc-600 cursor-pointer">
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" data-testid="portfolio-share-consent" />
                <span>{c.consentLabel}</span>
              </label>
              <div className="flex items-center gap-3 mt-4">
                <button type="submit" disabled={busy} className="ek-btn ek-btn-primary text-[13.5px] disabled:opacity-50" data-testid="portfolio-share-submit">
                  <Send size={13} /> {busy ? "Sending…" : c.submitLabel}
                </button>
                <span className="text-[11.5px] text-zinc-500">{c.counterLabel}</span>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
